import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { initialState, reduce, type HcAction, type HcState } from './healthCheckMachine';

interface HealthCheckContextValue {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  state: HcState;
  dispatch: React.Dispatch<HcAction>;
}

const HealthCheckContext = createContext<HealthCheckContextValue | null>(null);

export const HEALTH_CHECK_HASH = '#health-check';

/**
 * Owns the panel's open state and the questionnaire state. Open/closed is the
 * URL hash, so the browser's Back button closes the panel on a phone, and
 * `/#health-check` is a linkable way in. Mounted once, inside the layout.
 */
export function HealthCheckProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [state, dispatch] = useReducer((s: HcState, a: HcAction) => reduce(s, a), initialState);
  const isOpen = location.hash === HEALTH_CHECK_HASH;

  const open = useCallback(() => {
    if (location.hash === HEALTH_CHECK_HASH) return;
    navigate({ pathname: location.pathname, search: location.search, hash: HEALTH_CHECK_HASH });
  }, [navigate, location.pathname, location.search, location.hash]);

  const close = useCallback(() => {
    navigate({ pathname: location.pathname, search: location.search, hash: '' }, { replace: true });
  }, [navigate, location.pathname, location.search]);

  // A finished check starts afresh next time; a half-finished one resumes.
  useEffect(() => {
    if (!isOpen && state.phase === 'done') dispatch({ type: 'RESET' });
  }, [isOpen, state.phase]);

  const value = useMemo(() => ({ isOpen, open, close, state, dispatch }), [isOpen, open, close, state]);
  return <HealthCheckContext.Provider value={value}>{children}</HealthCheckContext.Provider>;
}

export function useHealthCheck(): HealthCheckContextValue {
  const ctx = useContext(HealthCheckContext);
  if (!ctx) throw new Error('useHealthCheck must be used inside HealthCheckProvider');
  return ctx;
}
